import { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import {
  Plus,
  Search,
  Pin,
  CheckSquare,
  Square,
  NotebookText,
  Trash2,
  Telescope,
  ClipboardList,
  Goal,
  X,
  Star,
  UserPlus,
  Save as SaveIcon,
  Image as ImageIcon,
  Sparkles,
} from "lucide-react";

const ATTACHMENT_LIMIT = 20;
const ATTACHMENT_ACCEPT = "image/jpeg,image/jpg,image/png,image/heic,image/heif,image/webp";

const TYPE_LABEL = { quick: "Quick", scouting: "Scouting", match: "Match", list: "Checklist" };
const TYPE_ICON = { quick: NotebookText, scouting: Telescope, match: Goal, list: ClipboardList };
const TYPE_COLOR = {
  quick: "text-slate-600",
  scouting: "text-violet-600",
  match: "text-emerald-600",
  list: "text-amber-600",
};

function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function groupNotes(rows) {
  const now = new Date();
  const pinned = [], today = [], thisWeek = [], older = [];
  for (const n of rows) {
    if (n.is_pinned) { pinned.push(n); continue; }
    const u = new Date(n.updated_at);
    const diff = (now - u) / 1000 / 86400;
    if (diff < 1) today.push(n);
    else if (diff < 7) thisWeek.push(n);
    else older.push(n);
  }
  return { pinned, today, thisWeek, older };
}

/* ============================ MAIN PAGE ============================ */
export default function NotesGlobal() {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [composing, setComposing] = useState(null); // 'quick' | 'scouting' | 'match' | 'list'
  const [showTypePicker, setShowTypePicker] = useState(false);

  const reload = async () => {
    const r = await api.get("/notes", { params: { note_type: filter === "all" ? undefined : filter, q: q || undefined } });
    setRows(r.data || []);
  };
  useEffect(() => { reload(); }, [filter, q]);

  const grouped = useMemo(() => groupNotes(rows), [rows]);

  return (
    <Layout title="Notes" subtitle="Field intel · scouting · checklists">
      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-5">
        {/* LEFT — note list */}
        <div className="pm-card p-4 max-h-[80vh] overflow-y-auto" data-testid="notes-list-panel">
          <div className="flex items-center gap-2 mb-3 relative">
            <button
              className="pm-btn-primary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5"
              onClick={() => setShowTypePicker((s) => !s)}
              data-testid="new-note-btn"
            >
              <Plus size={12} /> New note
            </button>
            {showTypePicker && (
              <div className="absolute top-11 left-0 z-30 bg-white border border-slate-200 shadow-lg rounded-lg p-1 w-44" data-testid="new-note-menu">
                {["quick", "scouting", "match", "list"].map((t) => {
                  const Icon = TYPE_ICON[t];
                  return (
                    <button
                      key={t}
                      className="w-full text-left flex items-center gap-2 px-3 py-2 rounded hover:bg-slate-50 text-sm"
                      onClick={() => { setComposing(t); setShowTypePicker(false); setSelected(null); }}
                      data-testid={`new-note-${t}`}
                    >
                      <Icon size={14} className={TYPE_COLOR[t]} />
                      {TYPE_LABEL[t]} note
                    </button>
                  );
                })}
              </div>
            )}
            <div className="relative flex-1">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                className="w-full h-9 pl-7 pr-3 rounded-lg border border-slate-200 text-xs"
                data-testid="notes-search"
              />
            </div>
          </div>

          <div className="flex items-center gap-1 mb-4 text-[11px]" data-testid="notes-filter-row">
            {["all", "quick", "scouting", "match", "list"].map((t) => (
              <button
                key={t}
                onClick={() => setFilter(t)}
                className={`px-2.5 py-1 rounded-full font-bold uppercase tracking-wider transition-colors ${
                  filter === t ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`filter-${t}`}
              >
                {t === "all" ? "All" : TYPE_LABEL[t]}
              </button>
            ))}
          </div>

          <Group label="📌 Pinned" rows={grouped.pinned} selected={selected} onSelect={setSelected} testid="group-pinned" />
          <Group label="Today" rows={grouped.today} selected={selected} onSelect={setSelected} testid="group-today" />
          <Group label="This week" rows={grouped.thisWeek} selected={selected} onSelect={setSelected} testid="group-week" />
          <Group label="Older" rows={grouped.older} selected={selected} onSelect={setSelected} testid="group-older" />

          {rows.length === 0 && (
            <div className="text-center text-xs text-slate-400 italic py-10" data-testid="notes-empty">
              No notes yet — tap <span className="font-bold">+ New note</span> to start.
            </div>
          )}
        </div>

        {/* RIGHT — editor / detail */}
        <div data-testid="notes-detail-panel">
          {composing ? (
            <NoteComposer
              type={composing}
              onCancel={() => setComposing(null)}
              onSaved={(n) => { setComposing(null); setSelected(n); reload(); }}
            />
          ) : selected ? (
            <NoteDetail
              note={selected}
              onChange={(n) => { setSelected(n); reload(); }}
              onDelete={() => { setSelected(null); reload(); }}
            />
          ) : (
            <div className="pm-card p-12 text-center text-slate-400">
              <NotebookText size={28} className="mx-auto mb-3 text-slate-300" />
              <div className="text-sm">Select a note or create a new one.</div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Group({ label, rows, selected, onSelect, testid }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="mb-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 mb-1.5">{label}</div>
      <div className="space-y-1">
        {rows.map((n) => <NoteRow key={n.id} note={n} active={selected?.id === n.id} onClick={() => onSelect(n)} />)}
      </div>
    </div>
  );
}

function NoteRow({ note, active, onClick }) {
  const Icon = TYPE_ICON[note.note_type] || NotebookText;
  const link = (note.links || []).find((l) => l.entity_name);
  const subtitle = note.note_type === "scouting"
    ? `Rating ${note.scouting_data?.overall_rating || "?"}/5${note.scouting_data?.player_name ? ` · ${note.scouting_data.player_name}` : ""}`
    : note.note_type === "match"
    ? `${(note.match_data?.players_observed || []).length} player${(note.match_data?.players_observed || []).length === 1 ? "" : "s"} noted`
    : note.note_type === "list"
    ? `${(note.list_items || []).length} item${(note.list_items || []).length === 1 ? "" : "s"}`
    : link?.entity_name || (note.body || "").slice(0, 60);
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-2.5 py-2 rounded-lg flex items-start gap-2 transition-colors ${
        active ? "bg-slate-100" : "hover:bg-slate-50"
      }`}
      data-testid={`note-row-${note.id}`}
    >
      <Icon size={13} className={`${TYPE_COLOR[note.note_type]} mt-0.5 shrink-0`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-slate-900 truncate flex items-center gap-1">
          {note.is_pinned && <Pin size={10} className="text-amber-500" />}
          {note.title || "(untitled)"}
        </div>
        <div className="text-[11px] text-slate-500 truncate">
          {TYPE_LABEL[note.note_type]} · {subtitle} · {fmtRelative(note.updated_at)}
        </div>
      </div>
    </button>
  );
}

/* ============================ DETAIL VIEW ============================ */
function NoteDetail({ note, onChange, onDelete }) {
  const Icon = TYPE_ICON[note.note_type];
  const handleDelete = async () => {
    if (!confirm("Delete this note?")) return;
    await api.delete(`/notes/${note.id}`);
    onDelete();
  };
  return (
    <div className="pm-card p-6" data-testid="note-detail">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={16} className={TYPE_COLOR[note.note_type]} />
          <div>
            <div className="font-display text-xl font-bold tracking-tight" data-testid="note-detail-title">
              {note.title || "(untitled)"}
            </div>
            <div className="text-xs text-slate-500">
              {TYPE_LABEL[note.note_type]} · {note.agent_name || "—"} · {fmtRelative(note.updated_at)}
            </div>
          </div>
        </div>
        <button onClick={handleDelete} className="text-slate-400 hover:text-red-600" data-testid="note-delete-btn">
          <Trash2 size={14} />
        </button>
      </div>

      {note.note_type === "quick" && (
        <div className="prose prose-sm max-w-none whitespace-pre-wrap text-slate-800" data-testid="note-detail-body">
          {note.body}
        </div>
      )}

      {note.note_type === "list" && (
        <ListBody note={note} onChange={onChange} />
      )}

      {note.note_type === "scouting" && (
        <ScoutingBody note={note} onChange={onChange} />
      )}

      {note.note_type === "match" && (
        <MatchBody note={note} />
      )}

      {/* Attachments — live upload/remove on existing notes */}
      <AttachmentGrid
        noteId={note.id}
        attachments={note.attachments || []}
        editable
        onChanged={(atts) => onChange({ ...note, attachments: atts })}
      />

      {(note.links || []).length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100 flex flex-wrap gap-1.5" data-testid="note-links">
          {note.links.map((l, i) => (
            <span key={i} className="pm-pill pm-pill-slate text-[10px]">
              {l.entity_type}: {l.entity_name || l.entity_id}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ListBody({ note, onChange }) {
  const [items, setItems] = useState(note.list_items || []);
  useEffect(() => { setItems(note.list_items || []); }, [note.id]);
  const toggle = async (i) => {
    const next = [...items];
    next[i] = { ...next[i], is_checked: !next[i].is_checked };
    setItems(next);
    const r = await api.patch(`/notes/${note.id}/items/${i}`, { is_checked: next[i].is_checked });
    if (r.data?.ok) onChange({ ...note, list_items: next });
  };
  return (
    <div className="space-y-1.5" data-testid="list-body">
      {items.map((it, i) => (
        <button
          key={i}
          onClick={() => toggle(i)}
          className={`w-full text-left flex items-start gap-2 py-1.5 px-2 rounded hover:bg-slate-50 ${
            it.is_checked ? "text-slate-400 line-through" : "text-slate-800"
          }`}
          data-testid={`list-item-${i}`}
        >
          {it.is_checked ? <CheckSquare size={14} className="text-emerald-600 mt-0.5 shrink-0" /> : <Square size={14} className="text-slate-400 mt-0.5 shrink-0" />}
          <span className="text-sm">{it.text}</span>
        </button>
      ))}
      {items.length === 0 && <div className="text-xs italic text-slate-400">No items.</div>}
    </div>
  );
}

function ScoutingBody({ note, onChange }) {
  const sd = note.scouting_data || {};
  const hasAttachments = (note.attachments || []).length > 0;
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);

  const onPromote = async () => {
    const r = await api.post(`/notes/${note.id}/add-to-network`);
    if (r.data?.ok) {
      onChange({ ...note, scouting_data: { ...sd, added_to_network: true, client_id: r.data.client_id, in_system: true } });
    }
  };

  const runOcr = async () => {
    setOcrBusy(true); setOcrResult(null);
    try {
      const r = await api.post(`/notes/${note.id}/scouting-ocr`);
      setOcrResult({ confidence: r.data.confidence, warnings: r.data.warnings || [], merged_fields: r.data.merged_fields || [] });
      if (r.data.note) onChange(r.data.note);
    } catch (e) {
      setOcrResult({ confidence: "low", warnings: [e?.response?.data?.detail || "OCR failed"], merged_fields: [] });
    } finally {
      setOcrBusy(false);
    }
  };

  return (
    <div data-testid="scouting-body">
      <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
        <Field label="Player">{sd.player_name || "—"}</Field>
        <Field label="Position">{sd.position || "—"}</Field>
        <Field label="Club">{sd.club || "—"}</Field>
        <Field label="Overall rating"><Stars value={sd.overall_rating} /></Field>
      </div>
      {sd.attributes && Object.keys(sd.attributes).length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
          {Object.entries(sd.attributes).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-xs">
              <span className="text-slate-600 capitalize">{k.replace(/_/g, " ")}</span>
              <Stars value={v.rating} />
            </div>
          ))}
        </div>
      )}
      {sd.free_observation && (
        <div className="rounded-lg bg-slate-50 px-3 py-2.5 text-sm text-slate-700 whitespace-pre-wrap mb-3">
          {sd.free_observation}
        </div>
      )}
      {sd.recommendation && (
        <div className="text-xs text-slate-600 mb-3">
          <span className="font-bold">Recommendation:</span> {sd.recommendation.replace(/_/g, " ")}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {!sd.added_to_network && !sd.in_system && sd.player_name && (
          <button onClick={onPromote} className="pm-btn-primary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="promote-to-network-btn">
            <UserPlus size={12} /> Add to network players
          </button>
        )}
        {sd.added_to_network && (
          <span className="pm-pill pm-pill-green text-[10px]" data-testid="promoted-pill">✓ Added to network</span>
        )}
        {hasAttachments && (
          <button
            onClick={runOcr}
            disabled={ocrBusy}
            className="h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50"
            data-testid="detail-ocr-btn"
            title="Uses ~$0.01 of Claude credits"
          >
            <Sparkles size={12} /> {ocrBusy ? "Extracting…" : "Re-scan with OCR"}
          </button>
        )}
      </div>

      {ocrResult && (
        <div
          className={`mt-3 rounded-lg border px-3 py-2 text-xs ${
            ocrResult.warnings.length === 0 ? "border-violet-200 bg-violet-50 text-violet-800" : "border-amber-200 bg-amber-50 text-amber-800"
          }`}
          data-testid="detail-ocr-result"
        >
          {ocrResult.merged_fields.length > 0 ? (
            <>
              <Sparkles size={11} className="inline -mt-0.5 mr-1" />
              Merged <b>{ocrResult.merged_fields.length}</b> new field{ocrResult.merged_fields.length === 1 ? "" : "s"} ·
              confidence <b>{ocrResult.confidence}</b>
            </>
          ) : (
            <>No new fields extracted (confidence: {ocrResult.confidence}). Existing data was preserved.</>
          )}
          {ocrResult.warnings.length > 0 && (
            <div className="mt-1 text-[11px]">⚠️ {ocrResult.warnings.join(" · ")}</div>
          )}
        </div>
      )}
    </div>
  );
}

function MatchBody({ note }) {
  const md = note.match_data || {};
  return (
    <div data-testid="match-body">
      <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
        <Field label="Match">{md.home_team && md.away_team ? `${md.home_team} vs ${md.away_team}` : "—"}</Field>
        <Field label="Venue">{md.venue || "—"}</Field>
        <Field label="Date">{md.match_date || "—"}</Field>
        <Field label="Competition">{md.competition || "—"}</Field>
      </div>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Players observed</div>
      <div className="space-y-2 mb-4">
        {(md.players_observed || []).map((p, i) => (
          <div key={i} className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`match-player-${i}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="font-bold text-slate-900">{p.name}</span>
              <Stars value={p.rating} />
            </div>
            <div className="text-xs text-slate-500 mb-1">
              {p.position || ""}{p.club ? ` · ${p.club}` : ""}
            </div>
            {p.note && <div className="text-xs text-slate-700">{p.note}</div>}
          </div>
        ))}
      </div>
      {md.general_notes && (
        <div className="rounded-lg bg-slate-50 px-3 py-2.5 text-sm text-slate-700 whitespace-pre-wrap">
          {md.general_notes}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">{label}</div>
      <div className="text-slate-800 mt-0.5">{children}</div>
    </div>
  );
}

function Stars({ value, onChange, size = 12 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          onClick={onChange ? () => onChange(i) : undefined}
          className={`${i <= (value || 0) ? "text-amber-500 fill-amber-500" : "text-slate-200"} ${onChange ? "cursor-pointer" : ""}`}
        />
      ))}
    </span>
  );
}

/* ============================ COMPOSER ============================ */

export function NoteComposer({ type, initialLinks = [], initialMatch = null, initialScouting = null, initialTitle = "", onCancel, onSaved, hideHeader = false }) {
  // Shared shape
  const [title, setTitle] = useState(initialTitle);
  const [body, setBody] = useState("");
  const [isPinned, setIsPinned] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [links, setLinks] = useState(initialLinks);
  // List
  const [items, setItems] = useState([{ text: "", is_checked: false }]);
  // Scouting
  const [scouting, setScouting] = useState({
    player_name: "",
    in_system: false,
    position: "",
    club: "",
    overall_rating: 0,
    attributes: {},
    free_observation: "",
    recommendation: "monitor",
    ...(initialScouting || {}),
  });
  // Match
  const [match, setMatch] = useState({
    home_team: "",
    away_team: "",
    venue: "",
    match_date: new Date().toISOString().slice(0, 10),
    competition: "",
    players_observed: [],
    general_notes: "",
    ...(initialMatch || {}),
  });
  const [saving, setSaving] = useState(false);
  const [linkClients, setLinkClients] = useState([]);
  // Photo attachment queue — uploaded AFTER the note is created (closes
  // photo-library feature). Each item: { id (local), file, previewUrl }.
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const titleRef = useRef(null);

  useEffect(() => {
    api.get("/clients").then((r) => {
      const arr = Array.isArray(r.data) ? r.data : r.data?.clients || [];
      setLinkClients(arr.map((c) => ({ id: c.id, name: c.full_name })));
    });
    setTimeout(() => titleRef.current?.focus(), 100);
  }, []);

  // Revoke object URLs when composer unmounts
  useEffect(() => () => pendingPhotos.forEach((p) => URL.revokeObjectURL(p.previewUrl)), [pendingPhotos]);

  const queuePhotos = (filesArr) => {
    const files = Array.from(filesArr || []);
    if (!files.length) return;
    const room = ATTACHMENT_LIMIT - pendingPhotos.length;
    const accepted = files.slice(0, room).map((f) => ({
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      file: f,
      previewUrl: URL.createObjectURL(f),
    }));
    setPendingPhotos((s) => [...s, ...accepted]);
  };

  const removeQueued = (id) => {
    setPendingPhotos((s) => {
      const target = s.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return s.filter((p) => p.id !== id);
    });
  };

  // Scouting OCR — runs Claude vision on the FIRST queued photo and fills
  // empty fields only (manual edits are never overwritten).
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrResult, setOcrResult] = useState(null); // { confidence, warnings, merged_fields }
  const runComposerOcr = async () => {
    if (!pendingPhotos.length) return;
    setOcrBusy(true);
    setOcrResult(null);
    try {
      const fd = new FormData();
      fd.append("file", pendingPhotos[0].file);
      const r = await api.post("/notes/scouting-ocr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const s = r.data.suggested || {};
      const merged = [];
      setScouting((prev) => {
        const next = { ...prev };
        for (const k of ["player_name", "position", "club", "free_observation", "recommendation"]) {
          if (s[k] && !prev[k]) { next[k] = s[k]; merged.push(k); }
        }
        if (s.overall_rating && !prev.overall_rating) { next.overall_rating = s.overall_rating; merged.push("overall_rating"); }
        if (s.attributes && typeof s.attributes === "object") {
          const existing = { ...(prev.attributes || {}) };
          for (const [ak, av] of Object.entries(s.attributes)) {
            if (!existing[ak]) { existing[ak] = av; merged.push(`attributes.${ak}`); }
          }
          next.attributes = existing;
        }
        return next;
      });
      // Use the title field for the player name if user hasn't typed one yet.
      if (!title && s.player_name) setTitle(s.player_name);
      setOcrResult({ confidence: r.data.confidence, warnings: r.data.warnings || [], merged_fields: merged });
    } catch (e) {
      setOcrResult({ confidence: "low", warnings: [e?.response?.data?.detail || "OCR failed"], merged_fields: [] });
    } finally {
      setOcrBusy(false);
    }
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = { note_type: type, title: title || null, body: body || null, is_pinned: isPinned, is_private: isPrivate, links };
      if (type === "list") payload.list_items = items.filter((i) => i.text);
      if (type === "scouting") {
        payload.scouting_data = scouting;
        payload.title = title || scouting.player_name;
      }
      if (type === "match") {
        payload.match_data = match;
        payload.title = title || `${match.home_team} vs ${match.away_team}`;
      }
      const r = await api.post("/notes", payload);
      // Upload any queued photos. We swallow individual failures so a single
      // bad file doesn't stop the rest.
      const uploaded = [];
      for (const p of pendingPhotos) {
        try {
          const fd = new FormData();
          fd.append("file", p.file);
          const u = await api.post(`/notes/${r.data.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          uploaded.push(u.data);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("attachment upload failed", p.file?.name, e?.response?.data?.detail);
        }
      }
      // Hand back a note with attachments hydrated client-side so the
      // detail view shows thumbnails immediately.
      onSaved({ ...r.data, attachments: [...(r.data.attachments || []), ...uploaded] });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pm-card p-6" data-testid={`composer-${type}`}>
      {!hideHeader && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {(() => { const I = TYPE_ICON[type]; return <I size={16} className={TYPE_COLOR[type]} />; })()}
            <h3 className="font-display text-xl font-bold tracking-tight">New {TYPE_LABEL[type].toLowerCase()} note</h3>
          </div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
      )}

      <input
        ref={titleRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={type === "scouting" ? "Player name or title (optional)" : "Title (optional)"}
        className="w-full mb-3 px-3 h-10 rounded-lg border border-slate-200 text-sm font-bold"
        data-testid="composer-title"
      />

      {type === "quick" && (
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Start typing…"
          rows={8}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm leading-relaxed"
          data-testid="composer-body"
        />
      )}

      {type === "list" && (
        <div className="space-y-1.5" data-testid="list-composer">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={it.is_checked}
                onChange={(e) => setItems((s) => s.map((x, j) => j === i ? { ...x, is_checked: e.target.checked } : x))}
              />
              <input
                type="text"
                value={it.text}
                onChange={(e) => setItems((s) => s.map((x, j) => j === i ? { ...x, text: e.target.value } : x))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setItems((s) => [...s, { text: "", is_checked: false }]);
                }}
                placeholder="Add item…"
                className="flex-1 h-9 px-2 rounded border border-slate-200 text-sm"
                data-testid={`list-item-input-${i}`}
              />
              <button onClick={() => setItems((s) => s.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-600"><X size={12} /></button>
            </div>
          ))}
          <button onClick={() => setItems((s) => [...s, { text: "", is_checked: false }])} className="text-emerald-700 text-xs font-bold flex items-center gap-1 mt-1" data-testid="add-list-item-btn">
            <Plus size={11} /> Add item
          </button>
        </div>
      )}

      {type === "scouting" && (
        <ScoutingComposerBody scouting={scouting} setScouting={setScouting} />
      )}

      {type === "match" && (
        <MatchComposerBody match={match} setMatch={setMatch} />
      )}

      {/* Photo attachments (queued; uploaded on Save) */}
      <div className="mt-4" data-testid="composer-photo-section">
        <div className="flex items-center gap-2 flex-wrap">
          <label className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer" data-testid="composer-attach-photo-btn">
            <ImageIcon size={12} /> Attach photo
            <input
              type="file"
              accept={ATTACHMENT_ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => { queuePhotos(e.target.files); e.target.value = ""; }}
              data-testid="composer-attach-photo-input"
            />
          </label>
          {type === "scouting" && pendingPhotos.length > 0 && (
            <button
              type="button"
              onClick={runComposerOcr}
              disabled={ocrBusy}
              className="h-9 px-3 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50"
              data-testid="composer-ocr-btn"
              title="Uses ~$0.01 of Claude credits"
            >
              <Sparkles size={12} /> {ocrBusy ? "Extracting…" : "Extract from photo"}
            </button>
          )}
        </div>
        {ocrResult && (
          <div
            className={`mt-2 rounded-lg border px-3 py-2 text-xs ${
              ocrResult.warnings.length === 0 ? "border-violet-200 bg-violet-50 text-violet-800" : "border-amber-200 bg-amber-50 text-amber-800"
            }`}
            data-testid="composer-ocr-result"
          >
            {ocrResult.merged_fields?.length > 0 ? (
              <>
                <Sparkles size={11} className="inline -mt-0.5 mr-1" />
                Auto-filled <b>{ocrResult.merged_fields.length}</b> field{ocrResult.merged_fields.length === 1 ? "" : "s"} ·
                confidence <b>{ocrResult.confidence}</b>
              </>
            ) : (
              <>No new fields extracted (confidence: {ocrResult.confidence}).</>
            )}
            {ocrResult.warnings.length > 0 && (
              <div className="mt-1 text-[11px]">⚠️ {ocrResult.warnings.join(" · ")}</div>
            )}
          </div>
        )}
        {pendingPhotos.length > 0 && (
          <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-1.5" data-testid="composer-pending-photos">
            {pendingPhotos.map((p) => (
              <div key={p.id} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                <img src={p.previewUrl} alt={p.file.name} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeQueued(p.id)}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 hover:bg-red-600 text-white flex items-center justify-center"
                  data-testid={`composer-remove-photo-${p.id}`}
                  title="Remove"
                >
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
        {pendingPhotos.length > 0 && (
          <div className="text-[10px] text-slate-400 mt-1">{pendingPhotos.length} photo{pendingPhotos.length === 1 ? "" : "s"} will upload after save</div>
        )}
      </div>

      {/* Link picker */}
      <div className="mt-5 pt-3 border-t border-slate-100">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1.5">Link to client</div>
        <select
          onChange={(e) => {
            const id = e.target.value;
            if (id && !links.find((l) => l.entity_id === id)) {
              setLinks((s) => [...s, { entity_type: "client", entity_id: id }]);
            }
            e.target.value = "";
          }}
          className="w-full h-9 px-2 rounded border border-slate-200 text-xs bg-white"
          data-testid="composer-link-client"
        >
          <option value="">+ Link a client…</option>
          {linkClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {links.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {links.map((l, i) => {
              const name = (linkClients.find((c) => c.id === l.entity_id) || {}).name || l.entity_id;
              return (
                <span key={i} className="pm-pill pm-pill-slate text-[10px] flex items-center gap-1">
                  {name}
                  <button onClick={() => setLinks((s) => s.filter((_, j) => j !== i))}><X size={9} /></button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-5 pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-xs text-slate-600">
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={isPinned} onChange={(e) => setIsPinned(e.target.checked)} data-testid="composer-pin" />
            Pin
          </label>
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={!isPrivate} onChange={(e) => setIsPrivate(!e.target.checked)} data-testid="composer-shared" />
            Share with workspace
          </label>
        </div>
        <div className="flex items-center gap-2">
          {onCancel && <button onClick={onCancel} className="text-slate-500 hover:text-slate-800 text-xs font-bold">Discard</button>}
          <button
            onClick={onSave}
            disabled={saving}
            className="pm-btn-primary h-9 px-4 rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
            data-testid="composer-save-btn"
          >
            <SaveIcon size={11} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

const SCOUTING_ATTRIBUTES = {
  TECHNICAL: ["first_touch", "dribbling", "passing", "finishing"],
  PHYSICAL: ["pace", "strength", "stamina"],
  MENTAL: ["decision_making", "work_rate", "attitude"],
};

function ScoutingComposerBody({ scouting, setScouting }) {
  const set = (k, v) => setScouting((s) => ({ ...s, [k]: v }));
  const setAttr = (k, rating) => setScouting((s) => ({ ...s, attributes: { ...s.attributes, [k]: { ...(s.attributes[k] || {}), rating } } }));
  return (
    <div className="space-y-3" data-testid="scouting-composer">
      <div className="grid grid-cols-2 gap-3">
        <input value={scouting.player_name} onChange={(e) => set("player_name", e.target.value)} placeholder="Player name" className="h-10 px-3 rounded border border-slate-200 text-sm" data-testid="scouting-player-name" />
        <input value={scouting.position} onChange={(e) => set("position", e.target.value)} placeholder="Position (e.g. LW)" className="h-10 px-3 rounded border border-slate-200 text-sm" />
        <input value={scouting.club} onChange={(e) => set("club", e.target.value)} placeholder="Club" className="h-10 px-3 rounded border border-slate-200 text-sm" />
        <div className="flex items-center gap-2 px-3 h-10 rounded border border-slate-200 text-sm">
          <span className="text-xs text-slate-500">Overall</span>
          <Stars value={scouting.overall_rating} onChange={(v) => set("overall_rating", v)} size={14} />
        </div>
      </div>
      {Object.entries(SCOUTING_ATTRIBUTES).map(([group, keys]) => (
        <div key={group}>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mt-2 mb-1">{group}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {keys.map((k) => (
              <div key={k} className="flex items-center justify-between text-sm px-2 py-1">
                <span className="text-slate-700 capitalize">{k.replace(/_/g, " ")}</span>
                <Stars value={scouting.attributes[k]?.rating || 0} onChange={(v) => setAttr(k, v)} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <textarea
        value={scouting.free_observation}
        onChange={(e) => set("free_observation", e.target.value)}
        placeholder="Free observation…"
        rows={4}
        className="w-full px-3 py-2 rounded border border-slate-200 text-sm leading-relaxed"
        data-testid="scouting-free-obs"
      />
      <div className="flex flex-wrap gap-2 text-xs">
        {["approach", "monitor", "no_action", "share"].map((r) => (
          <label key={r} className={`px-2.5 py-1 rounded-full border cursor-pointer ${scouting.recommendation === r ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
            <input type="radio" name="rec" className="hidden" checked={scouting.recommendation === r} onChange={() => set("recommendation", r)} />
            {r.replace(/_/g, " ")}
          </label>
        ))}
      </div>
    </div>
  );
}

function MatchComposerBody({ match, setMatch }) {
  const set = (k, v) => setMatch((s) => ({ ...s, [k]: v }));
  const addPlayer = () => setMatch((s) => ({ ...s, players_observed: [...s.players_observed, { name: "", position: "", club: "", rating: 0, note: "" }] }));
  const updatePlayer = (i, k, v) => setMatch((s) => ({ ...s, players_observed: s.players_observed.map((p, j) => j === i ? { ...p, [k]: v } : p) }));
  const removePlayer = (i) => setMatch((s) => ({ ...s, players_observed: s.players_observed.filter((_, j) => j !== i) }));
  return (
    <div className="space-y-3" data-testid="match-composer">
      <div className="grid grid-cols-2 gap-2">
        <input value={match.home_team} onChange={(e) => set("home_team", e.target.value)} placeholder="Home team" className="h-10 px-3 rounded border border-slate-200 text-sm" />
        <input value={match.away_team} onChange={(e) => set("away_team", e.target.value)} placeholder="Away team" className="h-10 px-3 rounded border border-slate-200 text-sm" />
        <input value={match.venue} onChange={(e) => set("venue", e.target.value)} placeholder="Venue" className="h-10 px-3 rounded border border-slate-200 text-sm" />
        <input type="date" value={match.match_date} onChange={(e) => set("match_date", e.target.value)} className="h-10 px-3 rounded border border-slate-200 text-sm" />
      </div>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Players observed</div>
      <div className="space-y-2">
        {match.players_observed.map((p, i) => (
          <div key={i} className="rounded-lg border border-slate-200 p-2.5" data-testid={`match-player-input-${i}`}>
            <div className="grid grid-cols-12 gap-2 mb-1.5">
              <input value={p.name} onChange={(e) => updatePlayer(i, "name", e.target.value)} placeholder="Name" className="col-span-5 h-9 px-2 rounded border border-slate-200 text-sm" />
              <input value={p.club} onChange={(e) => updatePlayer(i, "club", e.target.value)} placeholder="Club" className="col-span-4 h-9 px-2 rounded border border-slate-200 text-sm" />
              <div className="col-span-2 flex items-center">
                <Stars value={p.rating} onChange={(v) => updatePlayer(i, "rating", v)} />
              </div>
              <button onClick={() => removePlayer(i)} className="col-span-1 text-slate-300 hover:text-red-600"><X size={12} /></button>
            </div>
            <input value={p.note} onChange={(e) => updatePlayer(i, "note", e.target.value)} placeholder="Observation…" className="w-full h-9 px-2 rounded border border-slate-200 text-sm" />
          </div>
        ))}
        <button onClick={addPlayer} className="text-emerald-700 text-xs font-bold flex items-center gap-1" data-testid="add-match-player-btn">
          <Plus size={11} /> Add player
        </button>
      </div>
      <textarea value={match.general_notes} onChange={(e) => set("general_notes", e.target.value)} placeholder="General match notes…" rows={3} className="w-full px-3 py-2 rounded border border-slate-200 text-sm" />
    </div>
  );
}

/* ============================ EMBED (client homepage) ============================ */
export function ClientNotesSection({ clientId, clientName }) {
  const [rows, setRows] = useState([]);
  const [composing, setComposing] = useState(false);
  const reload = () => api.get("/notes", { params: { entity_type: "client", entity_id: clientId } }).then((r) => setRows(r.data || []));
  useEffect(() => { reload(); }, [clientId]);

  return (
    <div className="pm-card p-5" data-testid="client-notes-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-base font-bold tracking-tight flex items-center gap-1.5">
          <NotebookText size={14} className="text-slate-500" />
          Notes
          {rows.length > 0 && <span className="text-xs text-slate-400 font-normal">· {rows.length}</span>}
        </h3>
        <button
          onClick={() => setComposing(!composing)}
          className="pm-btn-secondary h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1"
          data-testid="add-client-note-btn"
        >
          <Plus size={11} /> Add note
        </button>
      </div>
      {composing && (
        <div className="mb-4">
          <NoteComposer
            type="quick"
            initialLinks={[{ entity_type: "client", entity_id: clientId }]}
            onCancel={() => setComposing(false)}
            onSaved={() => { setComposing(false); reload(); }}
            hideHeader
          />
        </div>
      )}
      <div className="space-y-2">
        {rows.length === 0 && !composing && (
          <div className="text-xs italic text-slate-400 py-4 text-center">No notes about {clientName || "this client"} yet.</div>
        )}
        {rows.map((n) => (
          <div key={n.id} className="rounded-lg border border-slate-100 px-3 py-2" data-testid={`client-note-${n.id}`}>
            <div className="flex items-center gap-2 mb-0.5">
              {(() => { const I = TYPE_ICON[n.note_type]; return <I size={11} className={TYPE_COLOR[n.note_type]} />; })()}
              <span className="text-xs font-bold text-slate-800">{n.title || "(untitled)"}</span>
              <span className="text-[10px] text-slate-400">· {fmtRelative(n.updated_at)}</span>
              {(n.attachments || []).length > 0 && (
                <span className="text-[10px] text-slate-400 inline-flex items-center gap-0.5">
                  · <ImageIcon size={10} /> {n.attachments.length}
                </span>
              )}
            </div>
            {n.body && <div className="text-xs text-slate-700 whitespace-pre-wrap line-clamp-3">{n.body}</div>}
            {(n.attachments || []).length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {n.attachments.slice(0, 4).map((a) => (
                  <AttachmentThumb key={a.id} noteId={n.id} attachment={a} size={36} />
                ))}
                {n.attachments.length > 4 && (
                  <div className="w-9 h-9 rounded border border-slate-200 bg-slate-50 flex items-center justify-center text-[10px] font-bold text-slate-500">
                    +{n.attachments.length - 4}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================ ATTACHMENTS ============================ */

function AttachmentThumb({ noteId, attachment, size = 56, onRemove }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revokedUrl = null;
    const token = localStorage.getItem("pm_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notes/${noteId}/attachments/${attachment.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => {
        if (!b) return;
        revokedUrl = URL.createObjectURL(b);
        setSrc(revokedUrl);
      })
      .catch(() => {});
    return () => { if (revokedUrl) URL.revokeObjectURL(revokedUrl); };
  }, [noteId, attachment.id]);

  const open = () => { if (src) window.open(src, "_blank", "noopener"); };

  return (
    <div className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-50" style={{ width: size, height: size }} data-testid={`note-attachment-${attachment.id}`}>
      {src ? (
        <button onClick={open} className="w-full h-full" title={attachment.filename}>
          <img src={src} alt={attachment.filename} className="w-full h-full object-cover" />
        </button>
      ) : (
        <div className="w-full h-full flex items-center justify-center text-slate-300"><ImageIcon size={size > 40 ? 18 : 12} /></div>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(attachment.id); }}
          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`note-attachment-remove-${attachment.id}`}
          title="Remove"
        >
          <X size={10} />
        </button>
      )}
    </div>
  );
}

function AttachmentGrid({ noteId, attachments, editable = false, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true); setErr("");
    const added = [];
    const room = ATTACHMENT_LIMIT - attachments.length;
    for (const f of Array.from(files).slice(0, room)) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post(`/notes/${noteId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(r.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || `Failed: ${f.name}`);
      }
    }
    setBusy(false);
    if (added.length && onChanged) onChanged([...attachments, ...added]);
  };
  const remove = async (attId) => {
    setBusy(true); setErr("");
    try {
      await api.delete(`/notes/${noteId}/attachments/${attId}`);
      if (onChanged) onChanged(attachments.filter((a) => a.id !== attId));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not remove");
    } finally {
      setBusy(false);
    }
  };
  if (!editable && attachments.length === 0) return null;
  return (
    <div className="mt-4 pt-4 border-t border-slate-100" data-testid="attachment-grid">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">
          Photos{attachments.length > 0 && <span className="ml-1 text-slate-400 normal-case tracking-normal">· {attachments.length}/{ATTACHMENT_LIMIT}</span>}
        </div>
        {editable && attachments.length < ATTACHMENT_LIMIT && (
          <label className="pm-btn-secondary h-7 px-2.5 rounded-lg text-[11px] font-bold inline-flex items-center gap-1 cursor-pointer" data-testid="detail-attach-photo-btn">
            <ImageIcon size={11} /> {busy ? "Uploading…" : "Attach photo"}
            <input
              type="file"
              accept={ATTACHMENT_ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
              disabled={busy}
              data-testid="detail-attach-photo-input"
            />
          </label>
        )}
      </div>
      {attachments.length === 0 ? (
        <div className="text-xs italic text-slate-400">No photos yet.</div>
      ) : (
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5" data-testid="attachment-thumbs">
          {attachments.map((a) => (
            <AttachmentThumb key={a.id} noteId={noteId} attachment={a} size={72} onRemove={editable ? remove : undefined} />
          ))}
        </div>
      )}
      {err && <div className="text-xs text-red-600 mt-2">{err}</div>}
    </div>
  );
}
